import { Locale } from 'app/generated/hygraph';
import { Env } from 'app/hooks/useStore';
import AuthService from 'app/services/AuthService';
import GuestCenterClient, { setBaseURL } from 'app/services/GuestCenterClient';
import {
  AuthCodeVerificationResponse,
  AuthTokenVerificationResponse,
  ConfigurationResponse,
  ConfigurationTypes,
  GuestDataResponse,
  ProductLineupResponse,
  UserVerificationResponse,
} from 'app/services/GuestCenterService.types';
import Logger from 'app/services/Logger';
import { isEmail } from 'app/utils/validation';

const MODULE = '[GuestCenterService]';
export const client = GuestCenterClient();

export const getBaseURL = () => client.defaults.baseURL;
export const setEnv = (env: Env) => {
  setBaseURL(client, env);
};

export enum VerifyEmailAction {
  Verify,
  SignIn,
  Cleanup,
}

export const signInWithEmail = async (email: string, locale: string) => {
  return client.post<UserVerificationResponse>(
    `/verify/email`,
    {
      email: `${email}`,
    },
    {
      headers: { 'Accept-Language': locale },
    }
  );
};

export const getAuthCodeValidition = (payload: {
  email: string;
  code: string;
  codeToken: string;
}) => {
  return client.post<AuthCodeVerificationResponse>(`/verify/code`, payload);
};

export const isEmailValid = (value: string | null | undefined): value is string => {
  // TODO work out why we get 'null' as the value
  if (typeof value !== 'string') {
    return false;
  }

  return isEmail(value.trim());
};
export const getAuthTokenValidation = async (authToken: string, email: Maybe<string>) => {
  Logger.info(`${MODULE} getAuthTokenValidation()`, { email });

  if (authToken !== '') {
    const bodyData: { token: string; email?: string } = {
      token: `${authToken}`,
    };
    if (isEmailValid(email)) {
      bodyData.email = email;
    }
    return client.post<AuthTokenVerificationResponse>('/verify/token', bodyData);
  }
  return null;
};

// TODO: tidy up with promise response / consolidate call pattern
export const getGuestData = async (
  email: string | null | undefined
): Promise<GuestDataResponse | undefined> => {
  let payload: GuestDataResponse | undefined;

  try {
    if (isEmailValid(email)) {
      const target = `/guest`;
      Logger.info(`${MODULE} get user - calling user api`, { target, email });
      const { data } = await client.post<GuestDataResponse>(
        target,
        { email: `${email}` },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );
      payload = data;

      Logger.info(`${MODULE} get user - api user response successful = ${payload?.success}`, {
        email: payload?.guestData?.email ?? 'no email returned!!',
      });
    } else {
      Logger.info(`${MODULE} get user - no valid email`);
    }
  } catch (error) {
    Logger.error(`${MODULE} get user - error`, { e: error });
    throw error;
  }
  return payload;
};

export const getGuestDataByLocator = async (
  locator: string | undefined
): Promise<GuestDataResponse | undefined> => {
  if (!locator) return undefined;
  const currentUser = AuthService.getCurrentUser();

  if (!(currentUser?.email?.includes('@') && currentUser.emailVerified)) {
    Logger.info(`${MODULE} get user by locator - no valid user`);
    return undefined;
  }

  let payload = null;
  try {
    const target = `/guest/${locator}`;
    const { email } = currentUser;

    const { data } = await client.post<GuestDataResponse>(
      target,
      { email: email ?? '' },
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );
    payload = data;

    Logger.info(
      `${MODULE} get user travel party by locator - api user response successful = ${payload?.success}`,
      {
        data,
      }
    );

    return payload;
  } catch (error) {
    Logger.error(`${MODULE} get user by locator - error`, { e: error });
    throw error;
  }
};

export type WalletPassURLProps = {
  email: string;
  walletType: 'apple' | 'google';
  attractionKey: string;
  attractionOrderNumber: string;
  locator: string;
  barcode: string;
  locale: Locale;
  categoryKey?: string;
  optionKey?: string;
};

export const getWalletPassUrl = ({
  email,
  walletType,
  attractionKey,
  attractionOrderNumber,
  locator,
  barcode,
  locale = Locale.En,
  categoryKey = '',
  optionKey = '',
}: WalletPassURLProps): string => {
  // going to go with convention for this without any security mechanism as the data here isn't available unless you can authenticate with the guest's email
  const categoryParam = categoryKey ? `&categoryKey=${categoryKey}` : '';
  const optionParam = optionKey ? `&optionKey=${optionKey}` : '';
  return `${getBaseURL()}/guest/wallet/${walletType}/email/${email}/order/${attractionOrderNumber}/party/${locator}/pass/${barcode}/attraction/${attractionKey}?lang=${locale}${categoryParam}${optionParam}`;
};

// TODO: implement Axios with request / response typing
export const getProductData = async (): Promise<ProductLineupResponse> => {
  let payload = null;
  try {
    const target = `/lineup`;
    Logger.info(`${MODULE} get product - calling product lineup api`, { target });
    const { data } = await client.get(target, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    payload = data;
    Logger.info(`${MODULE} get product - api lineup response successful = ${payload?.count > 0}`);
  } catch (error) {
    Logger.error(`${MODULE} get product - api lineup response error`, { e: error });
  }
  return payload;
};

export const getConfigurationGranular = async (configType?: ConfigurationTypes) => {
  const { data } = await client.post<ConfigurationResponse>(`/configuration/${configType ?? ''}`);
  return data;
};

export async function getConfiguration() {
  const { data } = await client.post<ConfigurationResponse>('/configuration');
  return data;
}
